import { useEffect, type FC } from "react";
import { StepSection } from "@components/StepSection";
import { StepTitleDescription } from "@/components/StepTitleDescritpion";
import styles from "./MyAccountPrescriptionStep.module.css";
import { useTranslation } from "@/i18n";
import {
  useActionsModule,
  useMyAccountPrescription,
  useWorkflow,
  useModalManager,
} from "@/hooks";
import { PrescriptionData } from "@/types/prescription.types";
import SavedPrescriptionCard from "@/components/SavedPrescriptionCard";
import { buildLensesDataToSend, isExpiredPrescription } from "@/utils";
import Button from "@/components/UI/Button";
import { useStepStore, useStoreActions } from "@/store/store";
import { useContactLensesStore } from "@/store";
import { PrescriptionDetailsModal } from "@/components/PrescriptionDetailsModal";
import { Skeleton } from "@/components/UI/Skeleton";
import { usePrescriptionData } from "@/hooks/usePrescriptionData";

interface MyAccountPrescriptionStepProps {
  title: string;
  description?: string;
  onComplete?: () => void;
}

export const MyAccountPrescriptionStep: FC<MyAccountPrescriptionStepProps> = ({
  title,
  description,
}) => {
  const { setManualInput, setWasFromAccount, setWidgetBlocked } =
    useStoreActions();
  const { t } = useTranslation();
  const {
    actions: contactLensesActions,
    contactLensesData,
    selectedPrescription,
  } = useContactLensesStore();
  const prescriptionDetailsModal = useModalManager("prescription-details");

  const { prescriptions }: { prescriptions: PrescriptionData[] | null } =
    useMyAccountPrescription();

  const { isWidgetBlocked } = useStepStore();

  const { genericAddToCart } = useActionsModule();
  const { handleNext, canGoNext } = useWorkflow();

  const { prescriptionAttributes } = usePrescriptionData();

  useEffect(() => {
    contactLensesActions.setSelectedPrescription(undefined);
  }, []);

  const handleSelectPrescription = (prescription: PrescriptionData) => {
    // if there is another step i set the selected prescription and proceed
    contactLensesActions.setSelectedPrescription(prescription);
    setWidgetBlocked(true);
    if (canGoNext) {
      setWidgetBlocked(false);
      handleNext();
    }
  };

  useEffect(() => {
    // when the selected prescription changes, if there isn't another step we create the lenses data and add to cart directly
    // this case is when user enters the configurator with 2 products already selected
    if (selectedPrescription && !canGoNext && prescriptionAttributes) {
      const lensDataToSend = buildLensesDataToSend(
        contactLensesData,
        selectedPrescription,
        prescriptionAttributes.OD,
        prescriptionAttributes.OS,
      );

      genericAddToCart(lensDataToSend);
    }
  }, [selectedPrescription, prescriptionAttributes]);

  const handleAddPrescription = () => {
    setManualInput(true);
    setWasFromAccount(true);
  };

  const handleMoreInfo = (prescription: PrescriptionData) => {
    prescriptionDetailsModal.open(
      <PrescriptionDetailsModal prescriptionData={prescription} />,
      "auto",
    );
  };

  return (
    <StepSection>
      <StepTitleDescription title={title} description={description} />
      <div className={styles.addPrescription}>
        <p className={styles.cantFind}>
          {t("myAccount.prescriptions.cantFind")}
        </p>
        <Button variant="link" onClick={handleAddPrescription}>
          {t("myAccount.prescriptions.addNewPrescription")}
        </Button>
      </div>
      <div className={styles.prescriptionList}>
        {prescriptions && prescriptions.length > 0 ? (
          prescriptions.map((prescription) => {
            let isExpired = false;
            if (prescription?.expireDate) {
              const today = new Date();
              const expirationDate = new Date(prescription?.expireDate);
              isExpired = today > expirationDate;
            }

            const isWarningVisible =
              prescription.origin === "ONLINE" &&
              isExpiredPrescription(prescription.lastUpdate, new Date(), 12);

            return (
              <SavedPrescriptionCard
                key={prescription.prescriptionId}
                prescriptionName={prescription.name}
                date={prescription.lastUpdate}
                prescriptionNameText={t(
                  "myAccount.prescriptions.prescriptionName",
                )}
                uploadedOnText={
                  isExpired
                    ? undefined
                    : t("myAccount.prescriptions.uploadedOn")
                }
                expiredOnText={
                  isExpired ? t("myAccount.prescriptions.expiredOn") : undefined
                }
                expiredDate={prescription.expireDate}
                prescriptionOrigin={prescription.origin}
                ctaText={t("myAccount.prescriptions.moreInfo")}
                onCtaClick={() => handleMoreInfo(prescription)}
                disabled={isExpired || isWidgetBlocked}
                isLoading={
                  isWidgetBlocked && prescription === selectedPrescription
                }
                warningText={
                  isWarningVisible
                    ? t("myAccount.prescriptions.expirationWarning")
                    : undefined
                }
                onClick={() => handleSelectPrescription(prescription)}
              />
            );
          })
        ) : (
          <div className={styles.cardSkeleton}>
            <Skeleton height={19} width="30%" />
            <Skeleton height={17} width="32%" />
            <Skeleton height={17} width="25%" />
            <Skeleton height={17} width="15%" />
            <Skeleton height={17} width="20%" />
            <Skeleton height={17} width="95%" />
          </div>
        )}
      </div>
    </StepSection>
  );
};
